import { webApi } from '@/shared/api/WebApi';
import { apiConfig } from '@/shared/api/apiConfig';
import type { IListRes, ICSVResponse } from '@/lib/types/common';
import type {
  ICurrentBillingPlanView,
  IBillingAccountViewData,
  IBillingAccountUpdateJsonResponse,
  IInvoiceViewData,
  IBillTotalIntervalData,
  IUpgradeTrialReq,
  ICardSetupRes,
  ICardConfirmReq,
  ICardConfirmRes,
  ICardOnFileRes,
  IUpgradeTrialSavedCardReq,
  IUpgradeTrialSavedCardRes,
} from '@/lib/types/Billing';
import type {
  CPUserViewData,
  CPUserCreateReq,
  CPUserUpdateReq,
  PasswordUpdateReq,
} from '@/lib/types/Auth';
import type { CustomerAllData } from '@/lib/types/Customer';
import type { SubaccountCreateReq, SubaccountUpdateReq } from '@/lib/types/Subaccount';
import type { IngestcfgData, IngestcfgCreateReq } from '@/lib/types/Ingest';

export class AccountApi {
  // ── Billing (always uses parentCId) ──────────────────────────────

  static async getBillingPlan(parentCId: number): Promise<ICurrentBillingPlanView> {
    return webApi.get<ICurrentBillingPlanView>('billing/currentplan', { cId: parentCId });
  }

  static async getPaymentMethod(parentCId: number): Promise<IBillingAccountViewData> {
    return webApi.get<IBillingAccountViewData>('billing/paymentmethod/default', { cId: parentCId });
  }

  static async updatePaymentMethod(
    parentCId: number,
    billingAccountId: string,
    data: IBillingAccountViewData,
  ): Promise<IBillingAccountUpdateJsonResponse> {
    return webApi.post<IBillingAccountUpdateJsonResponse>(
      `billing/paymentmethod/${billingAccountId}`,
      { cId: parentCId },
      data,
    );
  }

  static async getInvoices(parentCId: number): Promise<IListRes<IInvoiceViewData>> {
    return webApi.get<IListRes<IInvoiceViewData>>('billing/invoice', { cId: parentCId });
  }

  static async getBillTotalInterval(planName: string): Promise<IBillTotalIntervalData> {
    return webApi.post<IBillTotalIntervalData>('order/bill-total-interval', {}, { planName });
  }

  static async upgradeTrial(req: IUpgradeTrialReq): Promise<IBillingAccountUpdateJsonResponse> {
    return webApi.post<IBillingAccountUpdateJsonResponse>('order/upgradetrial', {}, req);
  }

  // ── Embedded Stripe card entry (flag billingCardEntryEnabled; ADR-010) ──────────
  // Paths per notes/billing-card-entry.md §1 until the jvm contract ref supersedes them.

  /** Mint a card-only SetupIntent for the client's Stripe Customer (one per form open). */
  static async cardSetup(parentCId: number): Promise<ICardSetupRes> {
    return webApi.post<ICardSetupRes>('billing/card/setup', { cId: parentCId }, {});
  }

  /** Persist the confirmed SetupIntent's payment method as the client's default card. */
  static async cardConfirm(parentCId: number, req: ICardConfirmReq): Promise<ICardConfirmRes> {
    return webApi.post<ICardConfirmRes>('billing/card/confirm', { cId: parentCId }, req);
  }

  /** Current card on file from the billing profile (never Stripe on the hot path). */
  static async getCardOnFile(parentCId: number): Promise<ICardOnFileRes> {
    return webApi.get<ICardOnFileRes>('billing/card', { cId: parentCId });
  }

  /** Trial → paid with the card already saved via cardSetup/cardConfirm; no card data on the wire. */
  static async upgradeTrialWithSavedCard(req: IUpgradeTrialSavedCardReq): Promise<IUpgradeTrialSavedCardRes> {
    return webApi.post<IUpgradeTrialSavedCardRes>('order/upgradetrial', {}, req);
  }

  /** Request re-opening of a closed (inactive) account, typically after a card update. */
  static async requestReopenAccount(parentCId: number): Promise<void> {
    return webApi.post<void>('billing/request-reopen', { cId: parentCId }, {});
  }

  /** Build a signature-authenticated invoice URL for viewing or PDF download. */
  static createInvoiceUrl(invoiceId: string, viewSignature: string, pdfDownload: boolean): string {
    const { baseUrl, version } = apiConfig;
    const dlParam = pdfDownload ? 'dl=1&' : '';
    return `${baseUrl}/v${version}/billing/invoice/${invoiceId}?${dlParam}signature=${viewSignature}`;
  }

  // ── Team (uses selectedCId) ──────────────────────────────────────

  static async getUsers(cId: number): Promise<IListRes<CPUserViewData>> {
    return webApi.get<IListRes<CPUserViewData>>('cpuser', { cId });
  }

  static async createUser(cId: number, req: CPUserCreateReq): Promise<CPUserViewData> {
    return webApi.post<CPUserViewData>('cpuser', { cId }, req);
  }

  static async updateUser(cId: number, cpuserId: number, req: CPUserUpdateReq): Promise<CPUserViewData> {
    return webApi.post<CPUserViewData>(`cpuser/${cpuserId}`, { cId }, req);
  }

  static async deleteUser(cId: number, cpuserId: number): Promise<void> {
    return webApi.delete<void>(`cpuser/${cpuserId}`, { cId });
  }

  static async checkUsernameAvailable(cId: number, username: string): Promise<void> {
    return webApi.post<void>('cpuser/username-available', { cId, username }, {});
  }

  static async updatePassword(cId: number, cpuserId: number, req: PasswordUpdateReq): Promise<void> {
    return webApi.post<void>(`cpuser/${cpuserId}/update-password`, { cId }, req);
  }

  // ── Sub-accounts (uses selectedCId; list from customerInfo) ──────

  static async createSubAccount(cId: number, req: SubaccountCreateReq): Promise<CustomerAllData> {
    return webApi.post<CustomerAllData>('subaccount', { cId }, req);
  }

  static async updateSubAccount(cId: number, subCId: number, req: SubaccountUpdateReq): Promise<CustomerAllData> {
    return webApi.post<CustomerAllData>(`subaccount/${subCId}`, { cId }, req);
  }

  static async deleteSubAccount(cId: number, subCId: number): Promise<void> {
    return webApi.delete<void>(`subaccount/${subCId}`, { cId });
  }

  static async getSubAccountStats(startTS: number, endTS: number): Promise<ICSVResponse> {
    return webApi.get<ICSVResponse>('subaccount/stats', { startTS, endTS, format: 'json' });
  }

  // ── Upload Profiles (uses selectedCId) ───────────────────────────

  static async getUploadProfiles(cId: number): Promise<IngestcfgData[]> {
    return webApi.get<IngestcfgData[]>('ingest/ingestcfgs', { cId });
  }

  static async createUploadProfile(cId: number, req: IngestcfgCreateReq): Promise<IngestcfgData> {
    return webApi.post<IngestcfgData>('ingest/ingestcfgs', { cId }, req);
  }

  static async updateUploadProfile(cId: number, ingestcfgId: number, req: IngestcfgCreateReq): Promise<IngestcfgData> {
    return webApi.post<IngestcfgData>(`ingest/ingestcfgs/${ingestcfgId}`, { cId }, req);
  }

  static async deleteUploadProfiles(cId: number, ingestcfgIds: number[]): Promise<void> {
    return webApi.post<void>('ingest/ingestcfgs/delete', { cId }, { ingestcfgIds });
  }
}
